import { FC, useState } from 'react'
import { Box, useTheme, Button, ButtonGroup } from '@mui/material'

import { Copyright } from '../../../components/molecules/Copyright'
import { useAnnotatablePrescriptionImageBox } from '../hooks/useAnnotatablePrescriptionImageBox'
import { useAnnotate } from '../hooks/useAnnotate'
import { useAnnotationState } from '../hooks/useAnnotationState'
import { useAITranscribe } from '../hooks/useAITranscribe'
import { useResultReview } from '../hooks/useResultReview'
import {
  NumberAnnotationPanel,
  AnnotationFieldType,
  fieldColors,
} from '../components/NumberAnnotationPanel'
import { AITranscribePanel } from '../components/AITranscribePanel'
import { ResultReviewPanel } from '../components/ResultReviewPanel'

type AnnotationMode = 'number' | 'ai-transcribe' | 'result-review'

const AnnotatorIndexPage: FC = () => {
  const [mode, setMode] = useState<AnnotationMode>('ai-transcribe')
  const [selectedField, setSelectedField] =
    useState<AnnotationFieldType>('保険者番号')

  const { selectedPresc, SearchBox } = useAnnotate()

  const theme = useTheme()

  // アノテーション状態管理をカスタムフックに委譲
  const {
    annotations,
    setAnnotations,
    isKigouTwoColumn,
    setIsKigouTwoColumn,
    unifiedMedicalCode,
    setUnifiedMedicalCode,
  } = useAnnotationState({ selectedPresc })

  // AI書き起こし
  const {
    result: aiResult,
    isLoading: aiIsLoading,
    error: aiError,
    transcribe: aiTranscribe,
  } = useAITranscribe(selectedPresc?.ocr_request_event_id)

  // 結果確認
  const {
    data: reviewData,
    isLoading: reviewIsLoading,
    error: reviewError,
  } = useResultReview(selectedPresc?.ocr_request_event_id)

  const { PrescImageBox, prescImageShown } = useAnnotatablePrescriptionImageBox(
    selectedPresc?.presc_images || [],
    selectedPresc?.basic_result?.処方箋存在領域?.selected_option || [],
    mode === 'number',
    selectedField,
    annotations,
    (newAnnotation) => {
      const updatedAnnotations = [
        ...annotations.filter((a) => a.field !== newAnnotation.field),
        newAnnotation,
      ]
      setAnnotations(updatedAnnotations)
    },
    fieldColors
  )

  if (!selectedPresc) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: 4,
          px: 2,
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 600 }}>{SearchBox}</Box>
        <Box sx={{ mt: 4 }}>
          <Copyright />
        </Box>
      </Box>
    )
  }

  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        minHeight: '100vh',
        backgroundColor: theme.palette.secondary.light,
      }}
    >
      {PrescImageBox}
      <Box
        sx={{
          [theme.breakpoints.down('md')]: prescImageShown
            ? {
                display: 'none',
              }
            : {
                width: '100%',
              },
        }}
        width="50%"
      >
        <div
          style={{
            position: 'relative',
            width: '100%',
            overflowY: 'scroll',
            overflowX: 'hidden',
            height: '100vh',
          }}
        >
          <Box sx={{ px: 2, pt: 2, pb: 1, bgcolor: 'primary.main' }}>
            {SearchBox}
            <ButtonGroup variant="contained" fullWidth sx={{ mt: 1 }}>
              <Button
                onClick={() => setMode('ai-transcribe')}
                color={mode === 'ai-transcribe' ? 'secondary' : 'primary'}
              >
                AI書き起こし
              </Button>
              <Button
                onClick={() => setMode('number')}
                color={mode === 'number' ? 'secondary' : 'primary'}
              >
                番号系annotation
              </Button>
              <Button
                onClick={() => setMode('result-review')}
                color={mode === 'result-review' ? 'secondary' : 'primary'}
              >
                結果確認
              </Button>
            </ButtonGroup>
          </Box>
          {mode === 'number' ? (
            <Box sx={{ height: 'calc(100vh - 150px)' }}>
              <NumberAnnotationPanel
                annotations={annotations}
                onAnnotationsChange={setAnnotations}
                selectedField={selectedField}
                onFieldChange={setSelectedField}
                prescription={selectedPresc}
                isKigouTwoColumn={isKigouTwoColumn}
                onIsKigouTwoColumnChange={setIsKigouTwoColumn}
                unifiedMedicalCode={unifiedMedicalCode}
                onUnifiedMedicalCodeChange={setUnifiedMedicalCode}
              />
            </Box>
          ) : mode === 'result-review' ? (
            <Box sx={{ height: 'calc(100vh - 150px)' }}>
              <ResultReviewPanel
                data={reviewData}
                isLoading={reviewIsLoading}
                error={reviewError}
              />
            </Box>
          ) : (
            <Box sx={{ height: 'calc(100vh - 150px)' }}>
              <AITranscribePanel
                result={aiResult}
                isLoading={aiIsLoading}
                error={aiError}
                onTranscribe={aiTranscribe}
              />
            </Box>
          )}
        </div>
      </Box>
    </div>
  )
}

export default AnnotatorIndexPage
